import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { Typography } from '@workspace/ui/components/Typography';
import { useBreakpoint } from '@workspace/ui/hooks/useBreakpoint';
import { ChevronDown24, ChevronRight16 } from '@workspace/ui/icons';
import { cn } from '@workspace/ui/lib/utils';

import { orderStore, userStore } from '~/stores';

interface HeaderAddressButtonProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children' | 'role'> {
  onClick: () => void;
}

export const HeaderAddressButton = observer<HeaderAddressButtonProps>(
  ({ className, ...props }) => {
    const { t } = useTranslation();
    const { isMobile } = useBreakpoint();

    const deliveryAddress = userStore.deliveryAddress;
    // A partner (EMU) point is shown as soon as it is picked: it carries no
    // zone, so waiting for an ETA would leave the header asking for an address
    // the customer has already chosen.
    const isPartnerPickup = !!deliveryAddress?.partnerPupExternalId;
    const hasAddress =
      !!deliveryAddress && (!!orderStore.etaCalculation || isPartnerPickup);
    const primaryText = isPartnerPickup
      ? deliveryAddress.shortAddress
      : (deliveryAddress?.address1 ?? '');
    const secondaryText = isPartnerPickup
      ? deliveryAddress.address1
      : (deliveryAddress?.zip ?? '');

    return (
      <div
        {...props}
        role="button"
        className={cn(
          'flex w-full cursor-pointer items-center truncate text-navigation-text-in-button transition-colors duration-300 group-data-[scrolled=true]:text-navigation-text-primary md:gap-1',
          className,
        )}
      >
        {isMobile ? (
          <>
            <div className="flex flex-col gap-px truncate">
              {hasAddress ? (
                <>
                  <Typography.Text100Medium className="truncate">
                    {primaryText}
                  </Typography.Text100Medium>
                  <Typography.Text100Semibold className="truncate">
                    {secondaryText}
                  </Typography.Text100Semibold>
                </>
              ) : (
                <Typography.Text100Semibold>
                  {t('phrases:selectDeliveryAddress')}
                </Typography.Text100Semibold>
              )}
            </div>
            <ChevronRight16 className="min-w-4" />
          </>
        ) : (
          <>
            <Typography.Text200Medium className="line-clamp-2 truncate">
              {hasAddress
                ? `${primaryText} ${secondaryText}`.trim()
                : t('phrases:selectDeliveryAddress')}
            </Typography.Text200Medium>
            <ChevronDown24 className="min-w-6" />
          </>
        )}
      </div>
    );
  },
);
HeaderAddressButton.displayName = 'HeaderAddressButton';
